import React from 'react';
import InputIconByKey from './InputIconByKey';
import RequiredAsteriskTooltip from './RequiredAsteriskTooltip';
import { IconKey } from '../../../types/system/icons';

interface InputFieldProps {
  label: string;
  name: string;
  type: 'text' | 'number' | 'email' | 'password' | 'date';
  value?: string | number;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  error?: string;
  min?: number;
  max?: number;
  step?: string;
  placeholder?: string;
  iconKey?: IconKey;
  disabled?: boolean;
  required?: boolean;
  /**
   * Sobrescribe la clave usada en el atributo `data-field` (para scroll
   * automático a errores). Por defecto se usa `name`, pero cuando el nombre
   * visual del campo difiere de la clave de error del backend (ej. `name`
   * usado solo para el label), se puede pasar la clave real aquí.
   */
  dataField?: string;

  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode'];
  pattern?: string;

  rightContent?: React.ReactNode;
  ariaLabel?: string;
}

const InputField: React.FC<InputFieldProps> = ({
  label,
  name,
  type,
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  error,
  min,
  max,
  step,
  placeholder,
  iconKey,
  disabled,
  required,
  inputMode,
  pattern,
  rightContent,
  ariaLabel,
  dataField,
}) => {
  const baseClasses =
    'w-full bg-transparent px-4 py-3.5 text-sm text-black outline-none placeholder:text-body dark:text-white dark:placeholder:text-bodydark2';

  return (
    <div className="mb-5 w-full" data-field={dataField ?? name}>
      {/* Label */}
      <label
        htmlFor={name}
        className="
          mb-2.5
          flex
          items-center
          gap-1
          text-sm
          font-medium
          text-black
          dark:text-white
        "
      >
        {label}
        {required && <RequiredAsteriskTooltip delay={400} />}
      </label>

      {/* Layout adaptable */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Input */}
        <div
          className={`
            bg-gray-1
            flex
            min-w-[180px]
            flex-1
            overflow-hidden
            rounded-xl
            border
            border-stroke
            transition-all
            duration-200
            focus-within:border-primary
            focus-within:ring-2
            focus-within:ring-primary/10
            dark:border-strokedark
            dark:bg-meta-4
            ${disabled ? 'cursor-not-allowed opacity-70' : ''}
          `}
        >
          {iconKey && (
            <div
              className="
                flex
                items-center
                justify-center
                border-r
                border-stroke
                bg-gray-2
                px-4
                dark:border-strokedark
                dark:bg-boxdark-2
              "
            >
              <InputIconByKey iconKey={iconKey} />
            </div>
          )}

        <input
          aria-label={ariaLabel}
            id={name}
            type={type}
            name={name}
            value={value ?? ''}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            min={min}
            max={max}
            step={step}
            placeholder={placeholder}
            disabled={disabled}
            inputMode={inputMode}
            pattern={pattern}
            className={baseClasses}
          />
        </div>

        {/* Subtotal */}
        {rightContent && (
          <div className="font-mono w-full text-right font-semibold text-black dark:text-white sm:w-auto sm:min-w-[120px]">
            {rightContent}
          </div>
        )}
      </div>

      {max !== undefined && type === 'number' && (
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Máximo permitido: {max}
        </p>
      )}

      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};

export default InputField;
